import { ChangeEvent, forwardRef } from "react";

import ChevronDown from "chameleon/icons/chevron-down";
import { InlineMessage, InputTitle } from "chameleon/lib/components";
import {
  inputCoreClass,
  inputErrorClass,
  inputTypographyClass,
  inputWrapperClass,
} from "chameleon/lib/components/input/input.css";

import {
  selectArrowClass,
  selectClass,
  selectWrapperClass,
} from "./select.css";

type SelectOption = { value: string; title: string };

export interface SelectProps {
  /**
   * Aria Label for accessibility
   */
  ariaLabel: string;
  /**
   * caption for the input
   */
  captionMessage?: string;
  /**
   * Error message for the input
   */
  errorMessage?: string;
  /**
   * Error Message Key for caption test id
   */
  errorMessageKey?: string;
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Info used for the input title's info icon
   */
  info?: string;
  /**
   * Is the input disabled
   */
  isDisabled?: boolean;
  /**
   * Is the input required?
   */
  isRequired?: boolean;
  /**
   * onBlur handler
   */
  onBlur?: () => void;
  /**
   * onChange handler
   */
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  /**
   * onFocus handler
   */
  onFocus?: () => void;
  /**
   * Options for the select
   */
  options?: SelectOption[];
  /**
   * Placeholder for the input
   */
  placeholder: string;
  /**
   * Title for the input
   */
  title?: string;
  /**
   * Value for the input
   */
  value?: string;
  /**
   * Tab index for the input
   */
  tabIndex?: number;
  /**
   * Flag for displaying label space when title is not there
   */
  isEmptyLabel?: boolean;
  /**
   * Flag for hiding messaging in iframe wrapper
   */
  inIframe?: boolean;
}

/**
 * Primary Select component. Is a controlled component and accepts a forwarded ref
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      ariaLabel,
      info,
      onFocus,
      onBlur,
      isDisabled = false,
      isRequired = false,
      errorMessage,
      errorMessageKey,
      captionMessage,
      id,
      title,
      options,
      value,
      onChange,
      placeholder,
      tabIndex,
      isEmptyLabel,
      inIframe = false,
    }: SelectProps,
    ref
  ) => {
    const hasMessage = captionMessage || errorMessage;
    const captionID = `${id}-${errorMessageKey}-caption`;
    const errorStyles = errorMessage ? inputErrorClass : "";
    if (isEmptyLabel) {
      title = "";
    }

    return (
      <div className={`${inputWrapperClass}`}>
        {(title || isEmptyLabel) && (
          <InputTitle
            isEmptyLabel={isEmptyLabel}
            info={info}
            htmlFor={id}
            title={title || ""}
          />
        )}
        <div className={`${selectWrapperClass}`}>
          <select
            aria-required={isRequired}
            aria-label={ariaLabel}
            className={`${selectClass} ${inputCoreClass} ${inputTypographyClass} ${errorStyles} validateField`}
            disabled={isDisabled}
            id={id}
            name={id}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            ref={ref}
            tabIndex={tabIndex}
            data-testid={id}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options?.map((option: SelectOption) => (
              <option
                key={`${option.value}-select-option`}
                value={option.value}
              >
                {option.title}
              </option>
            ))}
          </select>
          <span className={`${selectArrowClass}`}>
            <ChevronDown role="presentation" sizeVariant={24} />
          </span>
        </div>
        {hasMessage && !inIframe && (
          <InlineMessage
            id={captionID}
            message={errorMessage || captionMessage}
            type={errorMessage ? "error" : "info"}
          />
        )}
      </div>
    );
  }
);

Select.displayName = "Select";
